/**
 * Require a course of a profile (plan docs/plans/lms-plan.md 5.3, 8.2
 * "requirements (course plus due_days)", 18.20; WP-43; R19).
 *
 * The CoursePicker (every active course of the network, a draft-only course
 * included: 5.3 lets a profile require it and the reconcile assigns it once a
 * person publishes it, 18.17) with the courses the profile already requires
 * left out, and the due window in whole days (0 to 365, default 30; 5.3: days
 * after the person's onboarding start date, or after the day the requirement
 * first applied to them). The due window is fixed once saved: to change it,
 * remove the requirement and add it again (18.20). Saving posts
 * POST /admin/groups/:id/requirements {course_id, due_days}.
 */
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import Modal from '../../../../ui/Modal';
import { fieldCls, primaryBtnCls, secondaryBtnCls } from '../../../../ui/styles';
import { apiErrorMessage } from '../../../api/client';
import {
  addGroupRequirement,
  DEFAULT_REQUIREMENT_DUE_DAYS,
  REQUIREMENT_DUE_DAYS_MAX,
  REQUIREMENT_DUE_DAYS_MIN,
} from '../../../api/groups';
import type { AddRequirementResult } from '../../../api/groups';
import type { AdminCourseItem } from '../../../api/assignments';
import CoursePicker from '../../../components/pickers/CoursePicker';
import { ErrorNote, labelCls, metaCls, parseWholeDays, WarningNote } from '../../../components/pickers/shared';

export interface AddRequirementModalProps {
  isOpen: boolean;
  groupId: string;
  groupName: string;
  requiredCourseIds: string[];
  onClose: () => void;
  onAdded: (result: AddRequirementResult) => void;
}

/** A whole number of days in 0..365, or null (5.3 due_days; 18.20). */
export function parseDueDays(raw: string): number | null {
  return parseWholeDays(raw, REQUIREMENT_DUE_DAYS_MIN, REQUIREMENT_DUE_DAYS_MAX);
}

export function AddRequirementModal({ isOpen, groupId, groupName, requiredCourseIds, onClose, onAdded }: AddRequirementModalProps) {
  const [courseId, setCourseId] = useState<string | null>(null);
  const [course, setCourse] = useState<AdminCourseItem | null>(null);
  const [dueDays, setDueDays] = useState(String(DEFAULT_REQUIREMENT_DUE_DAYS));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setCourseId(null);
    setCourse(null);
    setDueDays(String(DEFAULT_REQUIREMENT_DUE_DAYS));
    setSaving(false);
    setError(null);
  }, [isOpen]);

  const days = parseDueDays(dueDays);
  const daysError =
    days === null ? `Enter a whole number of days from ${REQUIREMENT_DUE_DAYS_MIN} to ${REQUIREMENT_DUE_DAYS_MAX}.` : null;

  const save = async () => {
    if (!courseId || days === null) return;
    setSaving(true);
    setError(null);
    try {
      const result = await addGroupRequirement(groupId, { course_id: courseId, due_days: days });
      onAdded(result);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not add the requirement.'));
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Require a course of ${groupName}`} maxWidth="max-w-lg">
      <div className="space-y-4" data-testid="add-requirement">
        <CoursePicker
          value={courseId}
          onChange={(id, c) => {
            setCourseId(id);
            setCourse(c);
          }}
          publishedOnly={false}
          excludeIds={requiredCourseIds}
          label="Course"
        />
        {course && course.current_version === null && (
          <WarningNote>This course has no published version yet: nobody is assigned it until a person publishes it.</WarningNote>
        )}
        <div>
          <label htmlFor="lms-requirement-due-days" className={labelCls}>
            Due within (days)
          </label>
          <input
            id="lms-requirement-due-days"
            data-testid="requirement-due-days"
            type="number"
            inputMode="numeric"
            min={REQUIREMENT_DUE_DAYS_MIN}
            max={REQUIREMENT_DUE_DAYS_MAX}
            step={1}
            className={`${fieldCls} min-h-[44px] w-32 sm:min-h-0`}
            value={dueDays}
            onChange={(e) => setDueDays(e.target.value)}
            aria-invalid={daysError ? true : undefined}
          />
          {daysError ? (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400" data-testid="requirement-due-days-error">
              {daysError}
            </p>
          ) : (
            <p className={`${metaCls} mt-1`}>
              {"Counted from each person's onboarding start date, or from the day the requirement first applies to them. It cannot be edited later: remove the requirement and add it again."}
            </p>
          )}
        </div>
        {error && <ErrorNote testId="add-requirement-error">{error}</ErrorNote>}
        <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-4 dark:border-gray-700">
          <button type="button" className={secondaryBtnCls} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className={primaryBtnCls}
            onClick={save}
            disabled={saving || !courseId || days === null}
            data-testid="add-requirement-save"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Add requirement
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default AddRequirementModal;
